import React from 'react';
import { useChangeAll } from './ChangeAllContext';
import { useOrderFieldDisplayContext } from '../OrderFieldDisplayContext';
import { useChangeAllFields } from '../../../hooks/useChangeAllFields';

/** Per-field "apply to all pros" flag. Renders nothing outside change-all mode.
 *
 *  WHETHER THE FIELD IS OFFERED AT ALL is the registry's call, not this file's and not
 *  the call site's. A `bulkFieldName` prop used to be the whole answer — the prop was
 *  present, so a checkbox appeared — which meant opening or closing one field took a
 *  release. It now says "this field CAN be fanned out"; `field_definitions.is_change_all`
 *  says whether it currently IS, and an admin moves that in Data Management.
 *
 *  Tracks the field's DISPLAY state so it can't offer bulk-apply on a field the
 *  user can't edit:
 *    - field hidden   → nothing renders anyway (the field's whole row is skipped
 *                       by the config-driven hide), so no checkbox.
 *    - field readonly → checkbox renders disabled (mirrors the read-only field).
 *  fieldName is the camelCase view key; the display context resolves it to the
 *  legacy DB column internally, and it is the registry's `view_key` verbatim.
 *
 *  On a TEMPLATE anchor the field is judged by the template policy instead, and a
 *  locked field draws NO checkbox rather than a disabled one. Different from the order
 *  behavior on purpose: most order fields are editable and a disabled tick marks the
 *  exception, whereas most template fields are locked, so disabled ticks would be the
 *  rule and would read as a broken screen. A field CLOSED in the registry also draws
 *  nothing, for the same reason: it isn't an exception to a rule, it's off.
 *
 *  `editable` is the PERMISSION gate, and it comes first. The caller passes the very
 *  same expression that decides whether the field renders as an input or as a
 *  ReadValue, so the checkbox can never outlive the field's own gate — there is no
 *  second list of who-may-edit-what to drift out of sync.
 *
 *  It has to be a gate at all because ticking alone is enough to WRITE a field: an
 *  unedited flagged field fans the ANCHOR's current value (buildOrderChangeAllRows).
 *  A read-only field with a live checkbox beside it is therefore not read-only at
 *  all in change-all mode — someone who could not type a new Port of Origin could
 *  still copy the anchor's onto every pro in the set. That is also why the server no
 *  longer second-guesses this: /change-all/apply enforces what a single save
 *  enforces, and a single save leaves field-level permission right here. */
const BulkFieldCheckbox: React.FC<{
  fieldName: string;
  className?: string;
  /** May this user edit this field on THIS record? Defaults true so the handful of
   *  always-editable call sites, and provider-less test trees, stay as they were. */
  editable?: boolean;
}> = ({ fieldName, className, editable = true }) => {
  const { enabled, checkedFields, toggleField, isTemplate, fieldAllowed } = useChangeAll();
  const display = useOrderFieldDisplayContext();
  const { isFannable } = useChangeAllFields(enabled);
  if (!enabled) return null;
  if (!editable) return null;
  if (!isFannable(fieldName)) return null;
  if (isTemplate && !fieldAllowed(fieldName)) return null;
  // The order display rules key off order state, which a template has none of — the
  // policy above is its whole answer.
  const readonly = isTemplate ? false : display.isFieldReadonly(fieldName);
  return (
    <input
      type="checkbox"
      aria-label={`Apply ${fieldName} to all`}
      title={readonly ? 'Field is read-only' : 'Apply this field to all pros'}
      checked={checkedFields.has(fieldName)}
      disabled={readonly}
      onChange={() => { if (!readonly) toggleField(fieldName); }}
      className={`h-3.5 w-3.5 shrink-0 accent-blue-600 ${
        readonly ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
      } ${className ?? ''}`}
    />
  );
};

export default BulkFieldCheckbox;
